// ✅ src/pages/ViewOrders.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import {
  GetSingleOrdersThunk,
  GetSingleOrdersInvoiceThunk,
} from "../../redux/states/orders/thunk";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ViewOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleOrder, singleinvoice, loading } = useSelector(
    (state) => state.orders
  );
  const [showInvoice, setShowInvoice] = React.useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(GetSingleOrdersThunk(uuid));
  }, [dispatch, uuid]);

  useEffect(() => {
    if (singleOrder?.order_number) {
      dispatch(GetSingleOrdersInvoiceThunk(singleOrder.order_number));
    }
  }, [dispatch, singleOrder]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading order details...
            </p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleOrder) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Order Not Found
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  const order = singleOrder;

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "inactive":
        return { color: "bg-red-100 text-red-800", icon: XCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: AlertCircle };
    }
  };

  const StatusBadge = getStatusBadge(order.status);
  const StatusIcon = StatusBadge.icon;

  const handleDownloadPDF = async () => {
    const printElement = document.getElementById("invoice-print");
    if (!printElement) return alert("Invoice section not found!");

    try {
      printElement.classList.remove("hidden");
      await new Promise((r) => setTimeout(r, 300));

      const canvas = await html2canvas(printElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#fff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Invoice-${singleinvoice.order?.order_number}.pdf`);
      printElement.classList.add("hidden");
    } catch (error) {
      console.error("PDF generation failed:", error);
      alert("Failed to generate PDF. Check console logs.");
    }
  };

  const colorMap = {
    red: "#ef4444",
    blue: "#3b82f6",
    green: "#22c55e",
    black: "#000000",
    white: "#ffffff",
    yellow: "#eab308",
    orange: "#f97316",
    pink: "#ec4899",
    purple: "#a855f7",
    brown: "#92400e",
    gray: "#6b7280",
  };
  const getInvoiceVariants = (productId) => {
    try {
      const cartItem = singleinvoice?.cart_data?.find(
        (c) => c.product_id === productId
      );

      return cartItem?.json_variants ? JSON.parse(cartItem.json_variants) : {};
    } catch {
      return {};
    }
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-2 px-2 sm:px-2 lg:px-2">
        <div className="max-w-12xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-white p-6 rounded-xl shadow-sm border border-gray-200 gap-4">
            {/* Left Side - Back Button */}
            <div>
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
              >
                <ArrowLeft />
                Back
              </button>
            </div>

            {/* Right Side - Title, Status & Invoice Button */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 md:text-center">
              <h1 className="text-2xl font-bold text-gray-800">
                Order Details
              </h1>

              <span
                className={`px-3 py-1 rounded-full flex items-center justify-center gap-1 ${StatusBadge?.color}`}
              >
                <StatusIcon className="w-4 h-4" />
                {order?.status}
              </span>

              <button
                onClick={() => setShowInvoice(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                View Invoice
              </button>
            </div>
          </div>

          {/* Order Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="Order Number" value={order?.order_number} />
              <InfoRow label="UUID" value={order?.uuid} />
              <InfoRow label="User ID" value={order?.user_id_data} />
              <InfoRow label="Payment Method" value={order?.payment_method} />
              <InfoRow label="Payment Status" value={order?.payment_status} />
              <InfoRow label="Order Status" value={order?.order_status} />
              <InfoRow
                label="Shipping Address ID"
                value={order?.shipping_address_id}
              />
              <InfoRow
                label="Created At"
                value={new Date(order?.created_at).toLocaleString()}
              />
              <InfoRow
                label="Updated At"
                value={new Date(order?.updated_at).toLocaleString()}
              />
            </div>
          </div>

          {/* Pricing Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Pricing Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow label="Subtotal" value={`₹${order?.subtotal}`} />
              <InfoRow label="Tax" value={`₹${order?.tax}`} />
              <InfoRow label="Shipping" value={`₹${order?.shipping}`} />
              <InfoRow label="Discount" value={`₹${order?.discount}`} />
              <InfoRow label="Total" value={`₹${order?.total}`} />
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Order Items
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Image</th>
                    <th className="px-4 py-2 text-left">Product Name</th>
                    <th className="px-4 py-2 text-left">Quantity</th>
                    <th className="px-4 py-2 text-left">Price</th>
                    <th className="px-4 py-2 text-left">Discount</th>
                    <th className="px-4 py-2 text-left">Tax</th>
                    <th className="px-4 py-2 text-left">Total Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {order.order_items?.map((item) => {
                    const variants = item?.cart_row?.json_variants
                      ? JSON.parse(item.cart_row.json_variants)
                      : {};

                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        {/* Image */}
                        <td className="px-4 py-2">
                          <img
                            src={`${API_URL}/${item?.product_image}`}
                            alt="product"
                            className="w-[100px] rounded"
                          />
                        </td>

                        {/* ✅ Product Name + Variants */}
                        <td className="px-4 py-2">
                          <p className="font-medium text-gray-900">
                            {item?.product_name}
                          </p>

                          <p className="text-xs text-gray-500">
                            SKU: {item?.product_sku}
                          </p>

                          {/* ✅ Variants */}
                          {/* ✅ Variants */}
                          {Object.keys(variants).length > 0 && (
                            <div className="flex gap-2 mt-1 flex-wrap">
                              {Object.entries(variants).map(([key, value]) => {
                                const isColor = key.toLowerCase() === "color";
                                const colorValue =
                                  colorMap[value?.toLowerCase()] || value;

                                return (
                                  <span
                                    key={key}
                                    className="flex items-center gap-1 text-[15px] px-2 py-[2px] bg-gray-100 border rounded"
                                  >
                                    {isColor && (
                                      <span
                                        className="w-10 h-10 rounded border"
                                        style={{ backgroundColor: colorValue }}
                                      />
                                    )}
                                    {key}: <strong>{value}</strong>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>

                        {/* Quantity */}
                        <td className="px-4 py-2">{item?.quantity}</td>

                        {/* Price */}
                        <td className="px-4 py-2">₹{item?.price}</td>

                        {/* Discount */}
                        <td className="px-4 py-2">₹{item?.discount}</td>

                        {/* Tax */}
                        <td className="px-4 py-2">₹{item?.tax_amount}</td>

                        {/* Total */}
                        <td className="px-4 py-2 font-semibold">
                          ₹{item?.total_price}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Order Invoice Modal */}
        {showInvoice && singleinvoice && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div
              className="bg-white w-full max-w-4xl rounded-xl shadow-lg overflow-hidden"
              id="invoice-section"
            >
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                <h2 className="text-xl font-semibold text-gray-800">
                  Invoice Details
                </h2>
                <button
                  onClick={() => setShowInvoice(false)}
                  className="text-gray-500 hover:text-gray-700 text-lg font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Invoice Body */}
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto text-gray-800">
                {/* Company Header */}
                <div className="flex justify-between items-start border-b pb-4">
                  <div>
                    <div className="flex items-center mb-2">
                      {/* Company Logo - Replace with your actual logo */}
                      <div className="w-42 h-20 bg-white-600 rounded-lg flex items-center justify-center text-white font-bold mr-3">
                        <img
                          src="/assets/laxmisamgri.png"
                          alt="logo"
                          style={{ width: "150px" }}
                        />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p>
                        Floor No: GROUND FLOOR Building No./Flat No.: 08 Name Of
                        Premises/Building: LAXMI SAMGRI <br />
                        Road/Street:PATAN ROAD Nearby Landmark: NEAR WATER PYAU{" "}
                        <br />
                        Locality/Sub Locality: MUKLAN City/Town/Village: Muklan{" "}
                        <br /> District: Hisar <br />
                        State: Haryana PIN Code: 125004
                      </p>
                      <p>+91 70827-71712 | laxmisamgri@gmail.com</p>
                      <p className="font-semibold mt-1">GST: 06OGHPK8946N1ZA</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <h2 className="text-2xl font-bold text-blue-600 mb-2">
                      INVOICE
                    </h2>
                    <div className="text-sm">
                      <p>
                        <span className="font-semibold">Date:</span>{" "}
                        {new Date(
                          singleinvoice?.order?.created_at
                        ).toLocaleDateString()}
                      </p>
                      <p>
                        <span className="font-semibold">Invoice No:</span>{" "}
                        {singleinvoice?.order?.order_number}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Invoice To and From */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bill To */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                      Invoice To:
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-bold text-gray-900">
                        {singleinvoice?.user?.name}
                      </p>
                      <p className="text-gray-700">
                        {singleinvoice?.user?.email}
                      </p>
                      <p className="text-gray-700">
                        {singleinvoice?.user?.mobile}
                      </p>
                      <div className="mt-2 text-gray-600">
                        <p className="font-medium">
                          {singleinvoice?.address?.contact_person}
                        </p>
                        <p>{singleinvoice?.address?.address_line1}</p>
                        <p>{singleinvoice?.address?.address_line2}</p>
                        <p>
                          {singleinvoice?.address?.city},{" "}
                          {singleinvoice?.address?.state} -{" "}
                          {singleinvoice?.address?.pincode}
                        </p>
                        <p>{singleinvoice?.address?.country}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                      Order Details
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium">Order Number:</span>
                        <span>{singleinvoice?.order?.order_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Order Date:</span>
                        <span>
                          {new Date(
                            singleinvoice?.order?.created_at
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Payment Method:</span>
                        <span>
                          {singleinvoice?.order?.payment_method?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Payment Status:</span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            singleinvoice?.order?.payment_status === "paid"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {singleinvoice?.order?.payment_status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium">Order Status:</span>
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                          {singleinvoice?.order?.order_status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3 text-blue-600 border-b pb-1">
                    Order Items
                  </h3>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 text-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold border-r">
                            SL
                          </th>
                          <th className="px-4 py-3 text-left font-semibold border-r">
                            Item Descriptions
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r">
                            Price
                          </th>
                          <th className="px-4 py-3 text-center font-semibold border-r">
                            Qty
                          </th>
                          <th className="px-4 py-3 text-right font-semibold">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {singleinvoice?.items?.map((item, index) => {
                          const variants = getInvoiceVariants(item.product_id);

                          return (
                            <tr
                              key={item.id}
                              className="border-t hover:bg-gray-50"
                            >
                              {/* SL */}
                              <td className="px-4 py-3 border-r">
                                {index + 1}
                              </td>

                              {/* ✅ PRODUCT DETAILS + VARIANTS */}
                              <td className="px-4 py-3 border-r">
                                <p className="font-medium">
                                  {item.product_name}
                                </p>

                                <p className="text-xs text-gray-500">
                                  SKU: {item.product_sku}
                                </p>

                                {/* ✅ VARIANTS FROM cart_data */}
                                {Object.keys(variants).length > 0 && (
                                  <div className="flex gap-2 mt-1 flex-wrap">
                                    {Object.entries(variants).map(
                                      ([key, value]) => {
                                        const isColor =
                                          key.toLowerCase() === "color";
                                        const colorValue =
                                          colorMap[value?.toLowerCase()] ||
                                          value;

                                        return (
                                          <span
                                            key={key}
                                            className="flex items-center gap-1 text-[15px] px-2 py-[2px] bg-gray-100 border rounded"
                                          >
                                            {isColor && (
                                              <span
                                                className="w-8 h-8 rounded border"
                                                style={{
                                                  backgroundColor: colorValue,
                                                }}
                                              />
                                            )}
                                            {key}: <strong>{value}</strong>
                                          </span>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </td>

                              {/* Price */}
                              <td className="px-4 py-3 text-center border-r">
                                ₹{item.price}
                              </td>

                              {/* Qty */}
                              <td className="px-4 py-3 text-center border-r">
                                {item.quantity}
                              </td>

                              {/* Total */}
                              <td className="px-4 py-3 text-right font-semibold">
                                ₹{item.total_price}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary Section */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                  {/* Payment Info */}
                  <div className="md:col-span-2">
                    <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                      Payment Info:
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Accrued</span>
                          <span>Full Details</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Payment Method:</span>
                          <span>
                            {singleinvoice?.order?.payment_method?.toUpperCase()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Status:</span>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              singleinvoice?.order?.payment_status === "paid"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {singleinvoice?.order?.payment_status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                        Terms & Conditions:
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">
                          Your Name is not the name of any change in your name
                          or location. Goods once sold will not be taken back.
                          Payment should be made in full before delivery. All
                          disputes subject to Mandvi jurisdiction.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Amount Summary */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                      Amount Summary
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>₹{singleinvoice?.summary?.subtotal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Shipping:</span>
                        <span>₹{singleinvoice?.summary?.shipping}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tax:</span>
                        <span>₹{singleinvoice?.summary?.tax}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Discount:</span>
                        <span className="text-green-600">
                          -₹{singleinvoice?.summary?.discount}
                        </span>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-bold text-lg">
                        <span>Total:</span>
                        <span className="text-blue-600">
                          ₹{singleinvoice?.summary?.total}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t pt-6 mt-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <div className="mb-8">
                        <p className="font-semibold text-gray-700 mb-1">
                          Authorized Signatory
                        </p>
                        <div className="w-48 h-0.5 bg-gray-400 mb-1"></div>
                        <p className="text-sm text-gray-600">LAXMI SAMGRI</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        This is a computer generated invoice and does not
                        require physical signature.
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-lg font-semibold text-blue-600">
                        Thank You For Your Business!
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        For any queries, contact: maheshmota0@gmail.com
                      </p>
                      <button
                        onClick={handleDownloadPDF}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                      >
                        Download Invoice
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Order Invoice print details */}
      <div
        id="invoice-print"
        className="hidden absolute top-0 left-0 bg-white p-8 w-full text-gray-800"
      >
        {/* Invoice Body */}
        <div className="p-6 space-y-6 max-h-[auto] overflow-y-auto text-gray-800">
          {/* Company Header */}
          <div className="flex justify-between items-start border-b pb-4">
            <div>
              <div className="flex items-center mb-2">
                {/* Company Logo - Replace with your actual logo */}
                <div className="w-12 h-12 bg-white-600 rounded-lg flex items-center justify-center text-white font-bold mr-3">
                  <img src="/assets/vercel.png" style={{ width: "50px" }} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                LAXMI SAMGRI
                  </h1>
                  <p className="text-sm text-gray-600">Export Import Company</p>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p>
                  {" "}
                  Floor No.: GROUND FLOOR Building No./Flat No.: 08 Name Of
                  Premises/Building: LAXMI SAMGRI Road/Street: PATAN ROAD Nearby
                  Landmark: NEAR WATER PYAU Locality/Sub Locality: MUKLAN
                  City/Town/Village: Muklan District: Hisar State: Haryana PIN
                  Code: 125004
                </p>
                <p>+91 70827-71712 | laxmisamgri@gmail.com</p>
                <p className="font-semibold mt-1">GST: 06OGHPK8946N1ZA</p>
              </div>
            </div>

            <div className="text-right">
              <h2 className="text-2xl font-bold text-blue-600 mb-2">INVOICE</h2>
              <div className="text-sm">
                <p>
                  <span className="font-semibold">Date:</span>{" "}
                  {new Date(
                    singleinvoice?.order?.created_at
                  ).toLocaleDateString()}
                </p>
                <p>
                  <span className="font-semibold">Invoice No:</span>{" "}
                  {singleinvoice?.order?.order_number}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice To and From */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bill To */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                Invoice To:
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-bold text-gray-900">
                  {singleinvoice?.user?.name}
                </p>
                <p className="text-gray-700">{singleinvoice?.user?.email}</p>
                <p className="text-gray-700">{singleinvoice?.user?.mobile}</p>
                <div className="mt-2 text-gray-600">
                  <p className="font-medium">
                    {singleinvoice?.address?.contact_person}
                  </p>
                  <p>{singleinvoice?.address?.address_line1}</p>
                  <p>{singleinvoice?.address?.address_line2}</p>
                  <p>
                    {singleinvoice?.address?.city},{" "}
                    {singleinvoice?.address?.state} -{" "}
                    {singleinvoice?.address?.pincode}
                  </p>
                  <p>{singleinvoice?.address?.country}</p>
                </div>
              </div>
            </div>

            {/* Order Details */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                Order Details
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">Order Number:</span>
                  <span>{singleinvoice?.order?.order_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Order Date:</span>
                  <span>
                    {new Date(
                      singleinvoice?.order?.created_at
                    ).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Payment Method:</span>
                  <span>
                    {singleinvoice?.order?.payment_method?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Payment Status:</span>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      singleinvoice?.order?.payment_status === "paid"
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {singleinvoice?.order?.payment_status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Order Status:</span>
                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                    {singleinvoice?.order?.order_status}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3 text-blue-600 border-b pb-1">
              Order Items
            </h3>
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold border-r">
                      SL
                    </th>
                    <th className="px-4 py-3 text-left font-semibold border-r">
                      Item Descriptions
                    </th>
                    <th className="px-4 py-3 text-center font-semibold border-r">
                      Price
                    </th>
                    <th className="px-4 py-3 text-center font-semibold border-r">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-right font-semibold">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {singleinvoice?.items?.map((item, index) => {
                    const variants = getInvoiceVariants(item.product_id);

                    return (
                      <tr key={item.id} className="border-t hover:bg-gray-50">
                        {/* SL */}
                        <td className="px-4 py-3 border-r">{index + 1}</td>

                        {/* ✅ PRODUCT DETAILS + VARIANTS */}
                        <td className="px-4 py-3 border-r">
                          <p className="font-medium">{item.product_name}</p>

                          <p className="text-xs text-gray-500">
                            SKU: {item.product_sku}
                          </p>

                          {/* ✅ VARIANTS FROM cart_data */}
                          {Object.keys(variants).length > 0 && (
                            <div className="flex gap-2 mt-1 flex-wrap">
                              {Object.entries(variants).map(([key, value]) => {
                                const isColor = key.toLowerCase() === "color";
                                const colorValue =
                                  colorMap[value?.toLowerCase()] || value;

                                return (
                                  <span
                                    key={key}
                                    className="flex items-center gap-1 text-[15px] px-2 py-[2px] bg-gray-100 border rounded"
                                  >
                                    {isColor && (
                                      <span
                                        className="w-8 h-8 rounded border"
                                        style={{ backgroundColor: colorValue }}
                                      />
                                    )}
                                    {key}: <strong>{value}</strong>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>

                        {/* Price */}
                        <td className="px-4 py-3 text-center border-r">
                          ₹{item.price}
                        </td>

                        {/* Qty */}
                        <td className="px-4 py-3 text-center border-r">
                          {item.quantity}
                        </td>

                        {/* Total */}
                        <td className="px-4 py-3 text-right font-semibold">
                          ₹{item.total_price}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Payment Info */}
            <div className="md:col-span-2">
              <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                Payment Info:
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Accrued</span>
                    <span>Full Details</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Payment Method:</span>
                    <span>
                      {singleinvoice?.order?.payment_method?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium">Status:</span>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        singleinvoice?.order?.payment_status === "paid"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {singleinvoice?.order?.payment_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                  Terms & Conditions:
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Your Name is not the name of any change in your name or
                    location. Goods once sold will not be taken back. Payment
                    should be made in full before delivery. All disputes subject
                    to Mandvi jurisdiction.
                  </p>
                </div>
              </div>
            </div>

            {/* Amount Summary */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-blue-600 border-b pb-1">
                Amount Summary
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>₹{singleinvoice?.summary?.subtotal}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping:</span>
                  <span>₹{singleinvoice?.summary?.shipping}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>₹{singleinvoice?.summary?.tax}</span>
                </div>
                <div className="flex justify-between">
                  <span>Discount:</span>
                  <span className="text-green-600">
                    -₹{singleinvoice?.summary?.discount}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span className="text-blue-600">
                    ₹{singleinvoice?.summary?.total}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t pt-6 mt-6">
            <div className="flex justify-between items-end">
              <div>
                <div className="mb-8">
                  <p className="font-semibold text-gray-700 mb-1">
                    Authorized Signatory
                  </p>
                  <div className="w-48 h-0.5 bg-gray-400 mb-1"></div>
                  <p className="text-sm text-gray-600">URBAN SMOUTH</p>
                </div>
                <p className="text-xs text-gray-500">
                  This is a computer generated invoice and does not require
                  physical signature.
                </p>
              </div>

              <div className="text-right">
                <p className="text-lg font-semibold text-blue-600">
                  Thank You For Your Business!
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  For any queries, contact: laxmisamgri@gmail.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

// ---------------- Helper Component ----------------
const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
    <span className="font-medium text-gray-700">{label}</span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewOrders;
